import pendo, { LEARNER_API_KEY } from 'js/lib/pendo';
import { get as getUser } from 'js/lib/user';

import persistingCounter from 'bundles/ai-course-coach/utils/persistingCounter';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';

// Q3 LSAT pendo guide: https://app.pendo.io/s/5076330031611904/guides/jaDHN5aHb2g1M8ovWBybs-fBd7s?view=settings
const COACH_PENDO_GUIDE_ID = 'jaDHN5aHb2g1M8ovWBybs-fBd7s';

// singleton instance to use across course pages
let coachPendoTriggerInstance: ReturnType<typeof coachPendoTrigger>;

const coachPendoTrigger = ({
  triggerMessageCount,
  triggerOpenCount,
  courseId = '',
}: {
  triggerMessageCount: number;
  triggerOpenCount: number;
  courseId?: string;
}) => {
  const openCounter = persistingCounter('coach-chat-open-count-pendo', 0);
  const messageCounter = persistingCounter('coach-chat-message-count-pendo', 0);

  const setComplete = () => {
    openCounter.set(-1);
    messageCounter.set(-1);
  };

  try {
    const { external_id: externalId, is_staff: isStaff, is_superuser: isSuperuser } = getUser();

    pendo.init(
      {
        visitor: {
          id: externalId,
          isStaff,
          isSuperuser,
          courseId,
        },
      },
      LEARNER_API_KEY
    );
  } catch (e) {
    captureMessageInSentry({ error: e, source: 'pendo' });
  }

  const isComplete = (): boolean => {
    return openCounter.get() === -1 && messageCounter.get() === -1;
  };

  const conditionalTriggerPendo = async () => {
    const openCount = openCounter.get();
    const messageCount = messageCounter.get();

    if (openCount >= triggerOpenCount && messageCount >= triggerMessageCount) {
      // enable pendo
      try {
        pendo.showGuideById(COACH_PENDO_GUIDE_ID);
        setComplete();
      } catch (error) {
        captureMessageInSentry({ error });
      }
    }
  };

  return {
    handleNewMessage: () => {
      if (isComplete()) {
        return;
      }

      messageCounter.increment();
      conditionalTriggerPendo();
    },
    handleOpenChat: () => {
      if (isComplete()) {
        return;
      }

      openCounter.increment();
      conditionalTriggerPendo();
    },
  };
};

// returns singleton instance
export const getCoachPendoTrigger = ({ triggerMessageCount = 5, triggerOpenCount = 3, courseId = '' }) => {
  if (!coachPendoTriggerInstance) {
    coachPendoTriggerInstance = coachPendoTrigger({ triggerMessageCount, triggerOpenCount, courseId });
  }
  return coachPendoTriggerInstance;
};
